import { useState } from 'react';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '@/features/auth/model/auth-store';
import type { MagicLinkFormData } from '@/features/auth/model/schemas';

import { authService } from '@/shared/api';
import { ROUTES } from '@/shared/config/routes';
import { useCountdown } from '@/shared/hooks/use-countdown';
import { useInfinitePersonas } from '@/shared/hooks/use-infinite-personas';
import { analytics } from '@/shared/lib/analytics';
import { logger } from '@/shared/lib/logger';

type AuthStep = 'email' | 'code';

type UseAuthFlowReturn = {
  // State
  step: AuthStep;
  userEmail: string;
  isCodeError: boolean;
  isVerifying: boolean;
  isResending: boolean;
  isSubmitting: boolean;

  // Countdown
  timeLeft: number;
  canResend: boolean;

  // Actions
  submitEmail: (data: MagicLinkFormData) => Promise<void>;
  submitCode: (code: string) => Promise<void>;
  resendCode: () => Promise<void>;
  goBackToEmail: () => void;
};

export const useAuthFlow = (): UseAuthFlowReturn => {
  const navigate = useNavigate();
  const { refetch } = useInfinitePersonas();
  const login = useAuthStore((state) => state.login);
  const logout = useAuthStore((state) => state.logout);
  const { timeLeft, start: startCountdown, reset: resetCountdown } = useCountdown();

  const [step, setStep] = useState<AuthStep>('email');
  const [userEmail, setUserEmail] = useState('');
  const [isCodeError, setIsCodeError] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submitEmail = async (data: MagicLinkFormData) => {
    setIsSubmitting(true);

    // LOCAL DEVELOPMENT BYPASS - Remove this before committing
    if (window.location.hostname === 'localhost' && data.email === 'dev@test.com') {
      setUserEmail(data.email);
      setStep('code');
      startCountdown(60);
      toast.success('Development mode: Use code "123456" to log in', {
        id: 'email-otp-success',
        duration: 5000
      });
      setIsSubmitting(false);
      return;
    }

    try {
      const result = await authService.sendMagicLink(data);

      // Check response
      if (result.type !== 'success') {
        throw new Error(result.message || 'Failed to send code');
      }

      setUserEmail(data.email);
      setStep('code');
      startCountdown(60); // Start 60-second countdown
      toast.success('Verification code sent to your email!', { id: 'email-otp-success' });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to send verification code. Please try again.';

      // Show more helpful message for email service errors
      if (errorMessage.includes('failed to send email')) {
        toast.error('Email service is currently unavailable. For local dev, use email: dev@test.com', {
          id: 'email-otp-error',
          duration: 5000
        });
      } else {
        toast.error(errorMessage, { id: 'email-otp-error' });
      }

      logger.auth.error('Failed to send magic link', { error });
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitCode = async (code: string) => {
    setIsCodeError(false);
    setIsVerifying(true);

    // LOCAL DEVELOPMENT BYPASS - Remove this before committing
    if (window.location.hostname === 'localhost' && userEmail === 'dev@test.com' && code === '123456') {
      const mockUser = {
        id: 'dev-user-123',
        email: 'dev@mayatech.ai',  // Changed to mayatech.ai for AI Posting access
        credits: 10000,
        hasActiveSubscription: true,
        hasPersona: true,
        justSignUp: false,
        isAdmin: true
      };

      login(mockUser);
      // Persist dev session
      localStorage.setItem('famecreator-dev-user', JSON.stringify(mockUser));

      setIsVerifying(false);
      toast.success('Logged in with development account (10,000 credits)', { id: 'dev-login-success' });
      navigate(ROUTES.HOME);
      return;
    }

    try {
      await authService.verifyEmailOTP(userEmail, code);

      // Supabase automatically stores the session tokens
      // No need for manual localStorage operations

      // Now fetch actual user data from /auth/me
      const userResponse = await authService.getMe();

      // Check if user is authenticated according to backend
      if (userResponse.data.authenticated === false) {
        // Session exists but backend says not authenticated
        logout();
        toast.error('Authentication expired. Please log in again.', { id: 'auth-expired' });
        navigate(ROUTES.AUTH.REGISTER);
        return;
      }

      // Check if we have user data (authenticated response)
      if ('id' in userResponse.data) {
        // Login with real user data (Supabase already has the tokens)
        login(userResponse.data);

        // Identify user in RudderStack and track registration
        // Wrapped in try-catch to ensure analytics errors don't disrupt auth flow
        try {
          analytics.identify(userResponse.data.id, { email: userResponse.data.email });

          // Track User Registered event only for new signups
          if (userResponse.data.justSignUp) {
            analytics.track('User Registered', {
              email: userResponse.data.email,
              userId: userResponse.data.id,
            });
          }
        } catch (analyticsError) {
          logger.analytics.error('Failed to track auth event', { error: analyticsError });
        }
      } else {
        // No user data in response - logout
        logout();
        toast.error('Authentication failed. Please log in again.', { id: 'auth-failed' });
        navigate(ROUTES.AUTH.REGISTER);
        return;
      }

      // Clear React Query cache to fetch fresh data after login
      await refetch();

      navigate(ROUTES.HOME);
    } catch (error) {
      setIsCodeError(true);
      setIsVerifying(false);
      const errorMessage =
        error instanceof Error ? error.message : 'Invalid verification code. Please try again.';
      toast.error(errorMessage, { id: 'code-verification-error' });
    }
  };

  const resendCode = async () => {
    if (timeLeft > 0 || isResending) return;

    setIsResending(true);
    try {
      const result = await authService.sendMagicLink({ email: userEmail });

      // Check response
      if (result.type !== 'success') {
        throw new Error(result.message || 'Failed to resend code');
      }

      startCountdown(60); // Restart 60-second countdown
      toast.success('Verification code sent to your email!', { id: 'resend-otp-success' });
    } catch (error) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to resend verification code. Please try again.';
      toast.error(errorMessage, { id: 'resend-otp-error' });
      logger.auth.error('Failed to resend magic link', { error });
    } finally {
      setIsResending(false);
    }
  };

  const goBackToEmail = () => {
    setStep('email');
    setUserEmail('');
    setIsCodeError(false);
    resetCountdown();
  };

  return {
    // State
    step,
    userEmail,
    isCodeError,
    isVerifying,
    isResending,
    isSubmitting,

    // Countdown
    timeLeft,
    canResend: timeLeft === 0,

    // Actions
    submitEmail,
    submitCode,
    resendCode,
    goBackToEmail,
  };
};
